import React, { useState, useEffect } from 'react';
import { api } from './api';
import PostForm from './PostForm';
import PublishSuccess from './PublishSuccess';
import './AdminUsers.css';
import './FileUpload.css';

export default function AdminPosts({ onPostsChanged }) {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [publishedInfo, setPublishedInfo] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Фильтрация постов по поисковому запросу
    if (!searchQuery.trim()) {
      setFilteredPosts(posts);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = posts.filter(post => {
        const title = (post.title || '').toLowerCase();
        const content = (post.content || '').replace(/<[^>]*>/g, '').toLowerCase();
        const sectionTitle = (post.section_title || '').toLowerCase();
        const subcategoryTitle = (post.subcategory_title || '').toLowerCase();
        const authorName = (post.author_name || '').toLowerCase();
        
        return title.includes(query) ||
               content.includes(query) ||
               sectionTitle.includes(query) ||
               subcategoryTitle.includes(query) ||
               authorName.includes(query);
      });
      setFilteredPosts(filtered);
    }
  }, [searchQuery, posts]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      // Загружаем все посты без ограничений
      const [postsData, sectionsData] = await Promise.all([
        api.getPosts({ limit: 10000 }), // Большой лимит для загрузки всех постов
        api.getSections(),
      ]);
      // Если данные приходят в формате пагинации, извлекаем results
      const posts = Array.isArray(postsData) ? postsData : (postsData.results || postsData);
      setPosts(posts);
      setFilteredPosts(posts);
      setSections(sectionsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openCreateForm = () => {
    setEditingPost(null);
    setShowForm(true);
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingPost(null);
  };

  const handleSaved = (info) => {
    closeForm();
    if (info.created) setPublishedInfo(info);
    loadData();
    // Лента на главной держит свой список — сообщаем ей, что он устарел
    if (onPostsChanged) onPostsChanged();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот пост?')) {
      return;
    }

    try {
      await api.deletePost(id);
      loadData();
      if (onPostsChanged) onPostsChanged();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return <div className="admin-loading">Загрузка постов...</div>;
  }

  return (
    <div className="admin-users">
      {publishedInfo && (
        <PublishSuccess
          title={publishedInfo.title}
          sectionTitle={publishedInfo.sectionTitle}
          subcategoryTitle={publishedInfo.subcategoryTitle}
          onClose={() => setPublishedInfo(null)}
        />
      )}

      <div className="admin-toolbar">
        <div className="admin-toolbar-actions">
          <div className="admin-search">
            <svg className="admin-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              type="text"
              placeholder="Поиск постов..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="admin-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="admin-search-clear"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
          <button className="admin-add-button" onClick={openCreateForm}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Добавить пост
        </button>
        </div>
      </div>

      {error && (
        <div className="admin-error">
          {error}
        </div>
      )}

      {showForm && (
        <PostForm
          // key сбрасывает поля при переходе от одного поста к другому
          key={editingPost ? `edit-${editingPost.id}` : 'new'}
          post={editingPost}
          sections={sections}
          onCancel={closeForm}
          onSaved={handleSaved}
        />
      )}

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Заголовок</th>
              <th>Раздел</th>
              <th>Подкатегория</th>
              <th>Автор</th>
              <th>Статус</th>
              <th>Дата</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {posts.length === 0 ? (
              <tr>
                <td colSpan="8" className="admin-empty">Нет постов</td>
              </tr>
            ) : (
              posts.map(post => (
                <tr key={post.id}>
                  <td>{post.id}</td>
                  <td title={post.title}>
                    {post.title}
                  </td>
                  <td>{post.section_title || '-'}</td>
                  <td>{post.subcategory_title || '-'}</td>
                  <td>{post.author_name || '-'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {post.pinned && <span className="status-badge active">Закреплен</span>}
                      {post.is_public ? (
                        <span className="status-badge active">Публичный</span>
                      ) : (
                        <span className="status-badge inactive">Приватный</span>
                      )}
                    </div>
                  </td>
                  <td>{new Date(post.created_at).toLocaleDateString('ru-RU')}</td>
                  <td>
                    <div className="admin-actions">
                      <button
                        className="admin-action-button admin-action-edit"
                        onClick={() => handleEdit(post)}
                        title="Редактировать"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </button>
                      <button
                        className="admin-action-button admin-action-delete"
                        onClick={() => handleDelete(post.id)}
                        title="Удалить"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

